import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import type { ConsoleSection } from '../../lib/api/operatorConsole';
import { PermissionPicker } from './PermissionPicker';

interface Props {
  sections: ConsoleSection[];
  presets: Record<string, ConsoleSection[]>;
  onSubmit: (email: string, permissions: ConsoleSection[]) => Promise<boolean>;
}

export function AddMemberForm({ sections, presets, onSubmit }: Props) {
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState<ConsoleSection[]>([]);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!email.trim() || submitting) return;

    setSubmitting(true);
    const ok = await onSubmit(email.trim(), permissions);
    setSubmitting(false);
    if (ok) {
      setEmail('');
      setPermissions([]);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mb-6 max-w-2xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4"
    >
      <h2 className="mb-3 text-[14px] font-semibold text-[var(--co-text)]">Add a team member</h2>

      <label className="mb-1 block text-[12px] text-[var(--co-muted)]" htmlFor="team-email">
        Work email
      </label>
      <Input
        id="team-email"
        type="email"
        value={email}
        placeholder="colleague@example.com"
        onChange={(e) => setEmail(e.target.value)}
        className="mb-4"
      />

      <div className="mb-1 text-[12px] text-[var(--co-muted)]">Sections they may open</div>
      <PermissionPicker
        sections={sections}
        presets={presets}
        value={permissions}
        onChange={setPermissions}
        disabled={submitting}
      />

      <p className="mt-3 text-[11px] text-[var(--co-muted)]">
        Integration, Security and Team stay with you — they are not on this list.
      </p>

      <div className="mt-4">
        <Button type="submit" disabled={submitting || !email.trim()}>
          {submitting ? 'Adding…' : 'Add member'}
        </Button>
      </div>
    </form>
  );
}
